/**
 * Interactive text-agent chat client.
 *
 * `POST /agents/{agentId}/chat` returns `{ id, socket }`; we open the websocket
 * at `socket` and exchange turn-by-turn messages. Outbound: `{type:'user',text}`.
 * Inbound events: `agent`, `tool_call`, `tool_result`, `set`, `status`,
 * `turn_complete`, `error`, `user_echo` (see the backend text-chat session).
 */
import { api } from '../lib/client';

const backend = new URL(process.env.REACT_APP_BACKEND_SERVER);

/** Well-known id of the read-only built-in set-builder agent. */
export const SET_BUILDER_AGENT_ID = 'builtin:set-builder';

/**
 * Start a chat session with a text agent and open its websocket.
 * @param {string} agentId stored agent id or a built-in id
 * @param {{onMessage?:Function, onOpen?:Function, onClose?:Function, onError?:Function, set?:object, testResult?:object}} opts
 *   `set` seeds an existing set to edit; `testResult` seeds a prior test run to diagnose.
 * @returns {Promise<{sessionId:string, send:(text:string)=>void, close:()=>void}>}
 */
export async function startAgentChat(agentId, { onMessage, onOpen, onClose, onError, set, testResult } = {}) {
  const body = {};
  if (set) body.set = set;
  if (testResult) body.testResult = testResult;
  const { data } = await api.post(`/agents/${encodeURIComponent(agentId)}/chat`, body);
  const { id, socket } = data || {};
  if (!socket) {
    throw new Error('Chat session did not return a websocket path');
  }
  const wsUrl = `${backend.protocol === 'https:' ? 'wss:' : 'ws:'}//${backend.host}${socket}`;
  const ws = new WebSocket(wsUrl);
  ws.addEventListener('open', () => onOpen && onOpen());
  ws.addEventListener('message', (m) => {
    try { onMessage && onMessage(JSON.parse(m.data)); } catch (e) { /* ignore malformed frame */ }
  });
  ws.addEventListener('error', (e) => onError && onError(e));
  ws.addEventListener('close', () => onClose && onClose());

  return {
    sessionId: id,
    send: (text) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'user', text }));
    },
    close: () => ws.close(),
  };
}
